<script setup lang="ts">
// vue
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// third-party
import { useExtendedI18n } from '@/i18n'

// stores
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useEntityStore } from '@/modules/investing/stores/better-entity-store'
import { useCapitalAccountStore } from '@/modules/investing/stores/capital-account-store'

// composables
import { useSkeleton } from '@/modules/core/composables/useSkeleton'

// local components
import VSelectInvestor from '@/modules/investing/components/VSelectInvestor.vue'
import EntityLayout from '@/modules/investing/components/entities/entity-layout.vue'
import VSelect from '@/modules/investing/components/v-select.vue'

// shared components
import { VButtonGroup, VLoadingSection, VSection } from '@/modules/shared/components'

// config
import { initialMoney, times, toNumber } from '@/modules/shared/utils/money'

///////////////////////////////////////////////////////////////////////////////

const { n } = useExtendedI18n()
const route = useRoute()
const router = useRouter()

///////////////////////////////////////////////////////////////////////////////
// Calls
///////////////////////////////////////////////////////////////////////////////

const entityStore = useEntityStore()
const capitalAccountStore = useCapitalAccountStore()

const bookValue = computed(() => capitalAccountStore.bookValue)

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const { entity_id, entity_type, slug } = route.params as { entity_id: string; entity_type: string; slug: string }

const fetch = async (shareholder_cids = null, year = null) => {
  showSkeleton()
  year ||= currentYear
  await capitalAccountStore.fetchCapitalAccount('InvestorSet', entity_id, { slug, shareholder_cids, year })
  hideSkeleton()
}

const onFilter = async () => {
  // when all shareholders are selected, the API should not receive any shareholders
  const selectedShareholderCids =
    selectedCids.value.length === entityStore.listShareholders.length ? null : selectedCids.value
  await fetch(selectedShareholderCids, selectedYear.value)
}

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const authStore = useAuthStore()
const isAdmin = computed(() => authStore.is_site_admin)

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()
const currentYear = new Date().getFullYear()
const selectedYear = ref(currentYear)

const selectedCids = computed(() =>
  entityStore.selectedInvestorKeys.map((keys) => JSON.parse(keys).reverse().join(':')),
)

const yearOptions = computed(() => {
  const years = bookValue.value.year_options

  return years
    .concat([currentYear])
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort((a, b) => b - a)
    .map((year) => ({ label: year, value: year }))
})

////////////////////////////////////////
//// TABLE CONFIG
////////////////////////////////////////
const columns = computed(() => {
  return [
    {
      label: 'Since Inception',
      key: 'since_inception',
    },
    {
      label:
        currentYear.toString() === selectedYear.value.toString()
          ? 'Year to date'
          : `Year ${selectedYear.value - bookValue.value.year_options[0] + 1}`,
      key: 'year_to_date',
    },
    {
      label: 'Q4',
      key: 'Q4',
    },
    {
      label: 'Q3',
      key: 'Q3',
    },
    {
      label: 'Q2',
      key: 'Q2',
    },
    {
      label: 'Q1',
      key: 'Q1',
    },
  ]
})

const rows = [
  {
    label: 'Beginning Balance',
    key: 'beginning_balance',
  },
  {
    label: 'Contribution',
    key: 'contributions',
    break: true,
  },
  {
    label: 'Distribution',
    key: 'distributions',
  },
  {
    label: 'Total cash / deemed flows',
    key: 'total_cash',
  },
  {
    label: 'Operating Income/Expense',
    key: 'operating_income_or_expense',
    break: true,
  },
  {
    label: 'Management Fee',
    key: 'management_fee',
  },
  {
    label: 'Other Called Fee',
    key: 'other_called_fee',
  },
  {
    label: 'Unrealized gain/loss',
    key: 'unrealized_gain',
  },
  {
    label: 'Realized gain/loss',
    key: 'realized_gain',
  },
  {
    label: 'Interest Earned/Paid',
    key: 'interest_earned',
  },
  {
    label: 'Carried Interest',
    key: 'carried_interest',
  },
  {
    label: 'Other Distributed Fee',
    key: 'other_distributed_fee',
  },
  {
    label: 'Total Operating P&L',
    key: 'total_operating_profit_and_loss',
  },
  {
    label: 'Syndication Costs',
    key: 'syndication_fee',
    break: true,
  },
  {
    label: 'Ending Balance',
    key: 'ending_balance',
    break: true,
  },
]
const getValue = (row_key, column_key) => {
  const negative_values = [
    'management_fee',
    'other_called_fee',
    'carried_interest',
    'other_distributed_fee',
    'syndication_fee',
  ]
  let value = bookValue.value[column_key][row_key]

  if (negative_values.includes(row_key)) value = times(value, -1)

  const money_number = toNumber(value)

  if (money_number < 0) return `(${n(times(value, -1), 'currency')})`
  return n(value, 'currency')
}

////////////////////////////////////////
//// COMMITMENT SUMMARY
////////////////////////////////////////
const commitmentSummary = computed(() => capitalAccountStore.bookValue.commitment_summary)

const commitmentRows = [
  {
    label: 'Total Commitment',
    key: 'total_commitment',
  },
  {
    label: 'Initial Commitment',
    key: 'initial_commitment',
  },
  {
    label: 'Additional Commitments',
    key: 'additional_commitments',
    break: true,
  },
  {
    label: 'Contributions',
    key: 'contributions',
    break: true,
  },
  {
    label: 'Ending Unfunded Commitment',
    key: 'ending_unfunded_commitment',
    break: true,
  },
]

const getCommitmentValue = (row_key, column_key) => {
  const value = commitmentSummary.value?.[column_key]?.[row_key]
  if (!value) return n(initialMoney, 'currency')

  const money_number = toNumber(value)

  if (money_number < 0) return `(${n(times(value, -1), 'currency')})`
  return n(value, 'currency')
}

watch(selectedYear, async (newYear) => {
  if (newYear) await onFilter()
})

onMounted(async () => {
  if (!authStore.is_site_admin) router.push({ name: 'investing.entity-overview', params: route.params })
  await Promise.all([entityStore.fetchEntity(entity_id, { slug }), fetch()])
})
</script>

<template>
  <EntityLayout selectedTab="capital-account">
    <VSection label="Capital Account" class="mt-3">
      <template #actions>
        <VButtonGroup>
          <VSelectInvestor
            v-model="entityStore.selectedInvestorKeys"
            :investors="entityStore.listShareholders"
            :onClose="onFilter"
          />
          <VSelect v-model="selectedYear" :options="yearOptions" :v$="{}" :customLabel="true" size="sm">
            <div class="pr-5">Filter by year {{ selectedYear }}</div>
          </VSelect>
        </VButtonGroup>
      </template>
      <div class="relative z-0 w-full overflow-x-auto rounded-lg bg-white pb-5" v-if="!skeleton">
        <table class="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <td></td>
              <td v-for="column in columns" :key="column.key">
                <div class="font-bold">{{ column.label }}</div>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows">
              <td :class="{ break: row.break }">{{ row.label }}</td>
              <td :class="{ break: row.break }" v-for="column in columns" :key="column.key">
                {{ getValue(row.key, column.key) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <VLoadingSection
        class="mt-40"
        title="Loading Your Capital Account Information..."
        description="Please wait while we retrieve and process your capital account details. This ensures the latest data is available for your review."
        v-else
      />
    </VSection>

    <VSection label="Commitment & Contribution Summary" class="mt-3">
      <div class="relative z-0 w-full overflow-x-auto rounded-lg bg-white pb-5" v-if="!skeleton">
        <table class="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <td></td>
              <td v-for="column in columns" :key="column.key">
                <div class="font-bold">{{ column.label }}</div>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in commitmentRows" :key="row.key">
              <td :class="{ break: row.break }">{{ row.label }}</td>
              <td :class="{ break: row.break }" v-for="column in columns" :key="column.key">
                {{ getCommitmentValue(row.key, column.key) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <VLoadingSection
        class="mt-40"
        title="Loading Commitment Summary..."
        description="Please wait while we retrieve commitment and contribution data."
        v-else
      />
    </VSection>
  </EntityLayout>
</template>

<style scoped>
table thead tr td:nth-child(1),
table tbody tr td:nth-child(1) {
  @apply sticky left-0 z-10 w-[250px] min-w-[250px] max-w-[250px] bg-white !text-left;
}
table thead tr td {
  @apply h-[100px] border-t-[1px] border-gray-300 align-top;
}
table thead tr td:nth-child(1) {
  @apply rounded-tl-lg border-t-[1px] border-t-gray-300;
}
table tbody tr td {
  @apply border-b-[1px] border-gray-300 text-right align-top;
}
table tr td {
  @apply min-w-[150px] border-r-[1px] border-gray-300 px-2 py-2;
}
table tr td:first-child {
  @apply border-l-[1px] border-l-gray-300;
}
table tr td.break {
  @apply !pt-10;
}
table tr td.close-column {
  @apply !w-[1%] !min-w-[50px] bg-gray-200;
}
</style>
